import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  useLandlordPayoutNotificationRules,
  useNotificationAdminConfigs,
  useNotificationTemplates,
} from "services/hooks/rentals";
import { CHANNEL_OPTIONS, PAYOUT_NOTIFICATION_TYPE_OPTIONS } from "../constants";

const PayoutRulesTab = () => {
  const { templates, templateActionError } = useNotificationTemplates();
  const { adminConfigs, adminConfigActionError } = useNotificationAdminConfigs();
  const {
    payoutRules,
    isLoadingPayoutRules,
    errorPayoutRules,
    isSubmittingPayoutRule,
    payoutRuleActionError,
    createPayoutRule,
    updatePayoutRule,
    deletePayoutRule,
  } = useLandlordPayoutNotificationRules();
  const [message, setMessage] = useState({ type: "", text: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    related_organization: "",
    name: "",
    notification_type: "BEFORE_PAYDAY",
    day_offset: 0,
    channel: "EMAIL",
    related_template: "",
    related_admin_config: "",
    is_active: true,
  });

  const templateOptions = useMemo(
    () => templates.map((template) => ({ id: template.id, label: `${template.name} (${template.channel})` })),
    [templates]
  );

  const adminConfigOptions = useMemo(
    () => adminConfigs.map((config) => ({ id: config.id, label: config.name })),
    [adminConfigs]
  );

  const selectedNotificationType = PAYOUT_NOTIFICATION_TYPE_OPTIONS.find(
    (option) => option.value === form.notification_type
  );
  const notificationTypeLabelMap = PAYOUT_NOTIFICATION_TYPE_OPTIONS.reduce((accumulator, option) => {
    accumulator[option.value] = option.label;
    return accumulator;
  }, {});

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm({
      related_organization: "",
      name: "",
      notification_type: "BEFORE_PAYDAY",
      day_offset: 0,
      channel: "EMAIL",
      related_template: "",
      related_admin_config: "",
      is_active: true,
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (row) => {
    setEditingId(row.id);
    setForm({
      related_organization: row.related_organization ?? "",
      name: row.name || "",
      notification_type: row.notification_type || "BEFORE_PAYDAY",
      day_offset: row.day_offset ?? 0,
      channel: row.channel || "EMAIL",
      related_template: row.related_template?.id ?? row.related_template ?? "",
      related_admin_config: row.related_admin_config?.id ?? row.related_admin_config ?? "",
      is_active: row.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.related_template || !form.related_admin_config) {
      setMessage({ type: "error", text: "Name, template and admin config are required." });
      return;
    }
    const payload = {
      related_organization: form.related_organization ? Number(form.related_organization) : null,
      name: form.name.trim(),
      notification_type: form.notification_type,
      day_offset: Number(form.day_offset),
      channel: form.channel,
      related_template: Number(form.related_template),
      related_admin_config: Number(form.related_admin_config),
      is_active: form.is_active,
    };
    const response = editingId
      ? await updatePayoutRule(editingId, payload)
      : await createPayoutRule(payload);
    if (response.success) {
      setMessage({
        type: "success",
        text: editingId ? "Payout rule updated successfully." : "Payout rule created successfully.",
      });
      setDialogOpen(false);
    } else {
      setMessage({ type: "error", text: response.message || "Failed to save payout rule." });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this payout rule?")) return;
    const response = await deletePayoutRule(id);
    if (response.success) {
      setMessage({ type: "success", text: "Payout rule deleted." });
    } else {
      setMessage({ type: "error", text: response.message || "Failed to delete payout rule." });
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Landlord Payout Rules</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          Add Payout Rule
        </Button>
      </Box>

      {message.text && (
        <Alert severity={message.type === "error" ? "error" : "success"} sx={{ mb: 2 }} onClose={() => setMessage({ type: "", text: "" })}>
          {message.text}
        </Alert>
      )}

      {errorPayoutRules && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load payout rules.
        </Alert>
      )}

      {(payoutRuleActionError || templateActionError || adminConfigActionError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {payoutRuleActionError || templateActionError || adminConfigActionError}
        </Alert>
      )}

      <TableContainer component={Paper}>
        {isLoadingPayoutRules ? (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Offset</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>Admin Config</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payoutRules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9}>No payout rules found.</TableCell>
                </TableRow>
              ) : (
                payoutRules.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.name || "—"}</TableCell>
                    <TableCell>{notificationTypeLabelMap[row.notification_type] || row.notification_type || "—"}</TableCell>
                    <TableCell>{row.day_offset ?? 0}</TableCell>
                    <TableCell>{row.channel || "—"}</TableCell>
                    <TableCell>{row.related_template?.name || row.related_template?.id || "—"}</TableCell>
                    <TableCell>{row.related_admin_config?.name || row.related_admin_config?.id || "—"}</TableCell>
                    <TableCell>{row.is_active ? "Yes" : "No"}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenEdit(row)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editingId ? "Edit Payout Rule" : "Create Payout Rule"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Organisation ID (optional for global)"
            value={form.related_organization}
            onChange={(event) => setForm((prev) => ({ ...prev, related_organization: event.target.value }))}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Rule Name"
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Notification Type</InputLabel>
            <Select
              label="Notification Type"
              value={form.notification_type}
              onChange={(event) => setForm((prev) => ({ ...prev, notification_type: event.target.value }))}
            >
              {PAYOUT_NOTIFICATION_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            {selectedNotificationType?.description}
          </Typography>
          <TextField
            fullWidth
            type="number"
            margin="normal"
            label="Day Offset"
            value={form.day_offset}
            onChange={(event) => setForm((prev) => ({ ...prev, day_offset: event.target.value }))}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Channel</InputLabel>
            <Select
              label="Channel"
              value={form.channel}
              onChange={(event) => setForm((prev) => ({ ...prev, channel: event.target.value }))}
            >
              {CHANNEL_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Template</InputLabel>
            <Select
              label="Template"
              value={form.related_template}
              onChange={(event) => setForm((prev) => ({ ...prev, related_template: event.target.value }))}
            >
              {templateOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Admin Config</InputLabel>
            <Select
              label="Admin Config"
              value={form.related_admin_config}
              onChange={(event) => setForm((prev) => ({ ...prev, related_admin_config: event.target.value }))}
            >
              {adminConfigOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box display="flex" alignItems="center" mt={1}>
            <Switch
              checked={form.is_active}
              onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))}
            />
            <Typography variant="body2">Active</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={isSubmittingPayoutRule}>
            {isSubmittingPayoutRule ? <CircularProgress size={24} /> : editingId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayoutRulesTab;
